import { apiFetch, buildQuery } from "./client";
import type { Page } from "./admin";
import type { AttributeDefInline, AttributeDefView, AttributeValue } from "./catalog";

/**
 * Sprint 5l contract: docs/api/sprint5l-sastavnica-i-artikli.md §2–§3. Articles are the
 * quantity-tracked catalogue (bolts, bearings, filter bags…) a position's sastavnica draws on;
 * an article type records attributes from the codebook exactly like a part type does.
 */

// ---------------------------------------------------------------------------
// Article types ("Tipovi artikala")
// ---------------------------------------------------------------------------

export type ArticleTypeView = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  /** Attributes the type records. */
  attributeCount: number;
  /** Active articles of the type. */
  articleCount: number;
  createdAt: string;
  updatedAt: string;
};

/** `AttributeDefView` rows carry `articleTypeId` = this type, `assetTypeId` null. */
export type ArticleTypeDetail = ArticleTypeView & { attributeDefs: AttributeDefView[] };

export type ArticleTypeRequest = {
  code: string;
  name: string;
  description?: string | null;
  /** As on a part type (sprint 5f §3): POST writes all at once, PUT syncs by `attributeId` when sent. */
  attributeDefs?: AttributeDefInline[];
};

export type ArticleTypeListParams = { q?: string; page?: number; size?: number; sort?: string };

export const articleTypesApi = {
  /** `q` matches code and name; default `sort=code`. */
  list: (params: ArticleTypeListParams = {}) =>
    apiFetch<Page<ArticleTypeView>>(`/article-types${buildQuery(params)}`),
  /** Every active type, by name — the type picker on the article form. */
  all: () => apiFetch<ArticleTypeView[]>("/article-types/all"),
  get: (id: string) => apiFetch<ArticleTypeDetail>(`/article-types/${id}`),
  /** 201; 409 CODE_TAKEN; 400 VALIDATION (`details["attributeDefs[i].attributeId"]`). */
  create: (body: ArticleTypeRequest) =>
    apiFetch<ArticleTypeDetail>("/article-types", { method: "POST", body }),
  /** 409 CODE_TAKEN. */
  update: (id: string, body: ArticleTypeRequest) =>
    apiFetch<ArticleTypeDetail>(`/article-types/${id}`, { method: "PUT", body }),
  /** Soft delete; `reason` is mandatory (400 VALIDATION); 409 IN_USE (`details.articleCount`) while it has articles. */
  remove: (id: string, reason: string) =>
    apiFetch<void>(`/article-types/${id}?reason=${encodeURIComponent(reason)}`, { method: "DELETE" }),
  /** Attribute definitions of the type, by sortOrder then key. */
  attributeDefs: (id: string) => apiFetch<AttributeDefView[]>(`/article-types/${id}/attribute-defs`),
};

// ---------------------------------------------------------------------------
// Articles ("Artikli")
// ---------------------------------------------------------------------------

export type ArticleView = {
  id: string;
  typeId: string;
  typeCode: string;
  typeName: string;
  code: string;
  name: string;
  /** Row of the unit codebook (sprint 5k); the quantity on a sastavnica is counted in it. */
  unitId: string;
  /** Symbol of `unitId`, read-only. */
  unit: string;
  manufacturerId: string | null;
  manufacturer: string | null;
  note: string | null;
  /** Active positions whose sastavnica carries the article. */
  positionCount: number;
  createdAt: string;
  updatedAt: string;
};

export type ArticleDetail = ArticleView & {
  /** Values by attribute key, validated against `attributeDefs` (the type's definitions). */
  attributeValues: Record<string, AttributeValue>;
  attributeDefs: AttributeDefView[];
};

export type ArticleRequest = {
  typeId: string;
  code: string;
  name: string;
  unitId: string;
  manufacturerId?: string | null;
  attributeValues?: Record<string, unknown>;
  note?: string | null;
};

/** "Gde se koristi": one active position carrying the article; `path` = ancestors from the root. */
export type ArticleUsageRow = {
  positionId: string;
  code: string | null;
  name: string;
  kind: string;
  path: { id: string; name: string }[];
  quantity: number;
  note: string | null;
};

export type ArticleListParams = { q?: string; typeId?: string; page?: number; size?: number; sort?: string };

export const articlesApi = {
  /** `q` matches code, name and the type's name; default `sort=code`. */
  list: (params: ArticleListParams = {}) => apiFetch<Page<ArticleView>>(`/articles${buildQuery(params)}`),
  get: (id: string) => apiFetch<ArticleDetail>(`/articles/${id}`),
  /** Active positions whose sastavnica carries the article, by name. */
  usage: (id: string) => apiFetch<ArticleUsageRow[]>(`/articles/${id}/usage`),
  /**
   * 201; 409 CODE_TAKEN; 400 VALIDATION (`details.typeId` / `unitId` / `manufacturerId` = "not found");
   * 400 REQUIRED_ATTRIBUTE / UNKNOWN_ATTRIBUTE / INVALID_ATTRIBUTE_VALUE with `details.key`.
   */
  create: (body: ArticleRequest) => apiFetch<ArticleDetail>("/articles", { method: "POST", body }),
  /** Full replacement; a type change re-validates the values against the new type. */
  update: (id: string, body: ArticleRequest) => apiFetch<ArticleDetail>(`/articles/${id}`, { method: "PUT", body }),
  /** Soft delete; `reason` mandatory; 409 IN_USE with `details.positionCount` and `details.positions` (max 20). */
  remove: (id: string, reason: string) =>
    apiFetch<void>(`/articles/${id}?reason=${encodeURIComponent(reason)}`, { method: "DELETE" }),
};
